/**
 * usePermission — the one place components should read role-based UI gating
 * from. Rewritten 2026-08-14 (AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §10.1)
 * to read live grants from `GET /auth/me/capabilities` instead of the old
 * static `ROLE_PERMISSIONS` table — required for custom roles (created via
 * the new Roles admin UI) to work correctly in the frontend at all, since
 * they have no rank in the old hierarchy for `roleAtLeast` to check.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import type { RootState } from '@/stores'
import type { Role } from '@/types/role'
import type { Capability } from '@/config/permissions'
import { authApi } from '@/services/api'

interface MyCapabilities {
  role: string
  is_bypass_all: boolean
  capability_keys: string[]
}

export function usePermission() {
  const { user } = useSelector((state: RootState) => state.auth)

  // staleTime/refetchOnWindowFocus/refetchInterval per §10.1's firmed-up
  // fetch/cache strategy: a permission change becomes visible within ~1
  // minute on tab refocus, bounded at 5 minutes regardless of behavior via
  // the poll. The backend remains the real enforcement boundary throughout
  // that window — this only governs how fast the UI catches up.
  const { data, isLoading } = useQuery({
    queryKey: ['my-capabilities'],
    queryFn: () => authApi.getMyCapabilities().then((r) => r.data as MyCapabilities),
    enabled: !!user,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60_000,
  })

  const capabilitySet = useMemo(() => new Set(data?.capability_keys ?? []), [data])
  const isBypassAll = data?.is_bypass_all ?? false
  const role = user?.role as Role | undefined

  return {
    role,
    isBypassAll,
    // True while the user is known but their live capability set hasn't
    // loaded yet — RoleRoute waits on this too, so a route-level gate never
    // makes a false-negative redirect off of a still-empty capability set.
    capabilitiesLoading: !!user && isLoading,
    can: (capability: Capability) => isBypassAll || capabilitySet.has(capability),
    // §6's team-ownership carve-out, mirrored from the backend's
    // `_bypasses_ownership()` in teams.py exactly: `is_bypass_all` future-
    // proofs for a dynamic bypass-all role, `super_admin` is a one-time
    // grandfather clause for the 6 legacy roles that doesn't generalize to
    // custom roles. NOT the same thing as holding `teams.create`/`rename`/
    // `reassignManager`/`delete` (split 2026-08-23 from the old
    // `teams.manageAll`) — a custom role could hold any of those without
    // being an ownership bypass, though today they coincide for the 6
    // legacy roles.
    bypassesOwnership: isBypassAll || role === 'super_admin',
  }
}
